'use client';

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
  IconButton,
  Stack,
  TablePagination,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { PictureAsPdf, FileDownload } from '@mui/icons-material';
import axios from 'axios';

interface Invoice {
  id: number;
  uniqueNumber: string;
  status: string;
  amount: number;
  createdAt: string;
}

const API_BASE = 'https://staging-third-party-apis.nabil5332.workers.dev/api/v1';

export default function InvoicesView() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [pdfError, setPdfError] = useState<string | null>(null);

  // --- Charger les factures ---
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const apiKey = localStorage.getItem('apiKey');
      if (!apiKey) {
        alert('Aucune clé API trouvée. Créez-en une dans la page Manage.');
        return;
      }

      const res = await axios.get(`${API_BASE}/invoices`, {
        headers: { Authorization: `Bearer ${apiKey}` },
        params: { page: page + 1, pageSize: rowsPerPage, ...(statusFilter ? { status: statusFilter } : {}) },
      });

      setInvoices(res.data.invoices || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('❌ Erreur lors du chargement des factures :', err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (invoiceId: number, uniqueNumber: string) => {
    const apiKey = localStorage.getItem('apiKey');
    if (!apiKey) return;
    const selectedInvoice = invoices.find(inv => inv.id === invoiceId);

    console.log("=== DEBUG INVOICE ===");
    console.log("ID:", invoiceId);
    console.log("Unique Number:", uniqueNumber);
    console.log("Status:", selectedInvoice?.status);
    console.log("Amount:", selectedInvoice?.amount);
    console.log("Created At:", selectedInvoice?.createdAt);
    console.log("Invoice object:", selectedInvoice);
    console.log("=====================");

    const MAX_RETRIES = 3;      
    const RETRY_DELAY = 20000;    

    let attempt = 0;

    while (attempt < MAX_RETRIES) {
      try {
        const response = await axios.get(`${API_BASE}/invoices/${invoiceId}/pdf`, {
          headers: { Authorization: `Bearer ${apiKey}` },
          responseType: 'blob',
        });

 
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `Invoice-${uniqueNumber}.pdf`;
        link.click();
        window.URL.revokeObjectURL(blobUrl);
        return;

      } catch (err: any) {
    
        if (err.response?.status === 404) {
          attempt++;
          console.log(`PDF pas encore prêt (tentative ${attempt})...`);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        } else {
          console.error('❌ Erreur téléchargement PDF :', err);
          alert('Erreur lors du téléchargement du PDF.');
          return;
        }
      }
    }

    alert('Le PDF est toujours en cours de génération. Merci de réessayer dans quelques instants.');
  };



  // --- Télécharger Excel ---
  const handleDownloadExcel = async (invoiceId: number, uniqueNumber: string) => {
    try {
      const apiKey = localStorage.getItem('apiKey');
      if (!apiKey) return;

      const response = await axios.get(`${API_BASE}/invoices/${invoiceId}/excel-format`, {
        headers: { Authorization: `Bearer ${apiKey}` },
        responseType: 'blob',
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Invoice-${uniqueNumber}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('❌ Erreur téléchargement Excel :', err);
      alert('Erreur lors du téléchargement du fichier Excel.');
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [page, rowsPerPage, statusFilter]);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4A148C' }}>
          Liste des Factures
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filtrer par statut</InputLabel>
            <Select
              value={statusFilter}
              label="Filtrer par statut"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="PENDING">PENDING</MenuItem>
              <MenuItem value="PENDING_PAYMENT">PENDING_PAYMENT</MenuItem>
              <MenuItem value="PROCESSING_PAID">PROCESSING_PAID</MenuItem>
              <MenuItem value="PAID">PAID</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : invoices.length === 0 ? (
        <Alert severity="info">Aucune facture trouvée.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3 }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#714291' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Numéro unique</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Montant total</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.uniqueNumber}</TableCell>
                    <TableCell>{invoice.amount}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color:
                            invoice.status === 'PAID'
                              ? 'green'
                              : invoice.status === 'PENDING_PAYMENT'
                                ? 'orange'
                                : invoice.status === 'PENDING'
                                  ? '#d32f2f'
                                  : '#999',
                          fontWeight: 'bold',
                        }}
                      >
                        {invoice.status}
                      </Typography>
                    </TableCell>
                    <TableCell>{new Date(invoice.createdAt).toLocaleDateString('fr-FR')}</TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => handleDownloadPdf(invoice.id, invoice.uniqueNumber)}>
                        <PictureAsPdf />
                      </IconButton>
                      <IconButton color="success" onClick={() => handleDownloadExcel(invoice.id, invoice.uniqueNumber)}>
                        <FileDownload />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 20]}
          />
        </>
      )}
    </Box>
  );
}
